import React, { useContext, useState } from 'react';
import {
  Table,
  Stack,
  TablePagination,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  useTheme,
  Checkbox,
  Autocomplete,
  TextField,
  Chip,
  Box,
  Skeleton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { Careplan } from 'op-pages/RO/Careplan/Interface';
import { getCareplanName, getFormattedDateTime } from './helper';
import { redirectCareplanPath } from 'op-pages/RO/Careplan/Common';
import { useHistory } from 'react-router-dom';
import { patientGenderStockImage, formatPatientName } from 'op-utils/PatientHelper';
import { isDemo } from 'op-utils';
import { PatientName, Status, Priority, headCells, CareplanName, SearchInput } from './CareplanTableComponents';
import { GET_CAREPLANS_QUERY } from './graphql/queries';
import { useQuery } from '@apollo/client';
import { CareplanFilter } from 'op-enums';
import { UserContext } from 'op-contexts/index';

interface Props {
  careplanFilter: string[];
  statusFilter: string[];
  setCareplanFilter: (val: string[]) => void;
  setStatusFilter: (val: string[]) => void;
}

interface RowProps {
  id: string;
  patientName: string;
  department: string;
  careplanName: string;
  status: string;
  priority: string;
  lastModified: string;
  patientId: string;
  ida: string;
  photoUrl: string;
  patientGender: string;
}

interface ConfigFilterOption {
  checked: boolean;
  id: string;
  name: string;
}

interface OptionType {
  label: string;
  value: string;
}

const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIOSN = [DEFAULT_ROWS_PER_PAGE, 20];

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Skeleton loading component
const SkeletonTableRows = ({ rowCount = 5 }: { rowCount?: number }) => (
  <>
    {Array.from({ length: rowCount }, (_, index) => (
      <TableRow key={`skeleton-${index}`} sx={{ height: 84 }}>
        <TableCell sx={{ height: 84 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Skeleton variant="circular" width={30} height={30} />
            <Stack>
              <Skeleton variant="text" width={120} height={20} />
              <Skeleton variant="text" width={100} height={16} />
            </Stack>
          </Stack>
        </TableCell>
        <TableCell sx={{ height: 84 }}>
          <Skeleton variant="text" width={80} height={20} />
        </TableCell>
        <TableCell sx={{ height: 84 }}>
          <Stack>
            <Skeleton variant="text" width={200} height={20} />
            <Skeleton variant="text" width={120} height={16} />
          </Stack>
        </TableCell>
        <TableCell sx={{ height: 84 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Skeleton variant="circular" width={16} height={16} />
            <Skeleton variant="text" width={60} height={20} />
          </Stack>
        </TableCell>
        <TableCell sx={{ height: 84 }}>
          <Skeleton variant="text" width={60} height={20} />
        </TableCell>
        <TableCell sx={{ height: 84 }}>
          <Skeleton variant="text" width={100} height={20} />
        </TableCell>
      </TableRow>
    ))}
  </>
);

const CareplanTable = ({ statusFilter, careplanFilter, setCareplanFilter, setStatusFilter }: Props): JSX.Element => {
  const [searchString, setSearchString] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);

  const theme = useTheme();
  const history = useHistory();

  const variables = {
    page: currentPage + 1, // Django paginator is 1-indexed
    rowsPerPage: rowsPerPage,
    search: searchString,
    status: statusFilter,
    careplanChanged: careplanFilter,
    onlyForRequestingPractitioner: true,
  };
  const { data, loading } = useQuery(GET_CAREPLANS_QUERY, {
    variables: variables,
    fetchPolicy: 'network-only',
  });
  const careplans = data?.careplans;
  const careplanCount = data?.careplansCount || 0;
  const rows =
    careplans?.map((careplan: Careplan) => {
      return {
        id: careplan.id,
        patientName: formatPatientName(
          careplan.patient.firstName,
          careplan.patient.middleName,
          careplan.patient.preferredName,
          careplan.patient.lastName,
          careplan.patient.fullName,
        ),
        department: careplan.treatingDepartment || '-',
        careplanName: getCareplanName(careplan) || 'Diagnosis not selected',
        status: careplan.careplanStatus,
        priority: careplan.priority,
        lastModified: getFormattedDateTime(careplan.updatedAt),
        patientId: careplan.patient.id,
        ida: careplan.patient.ida,
        photoUrl: careplan.patient.photoUrl,
        patientGender: careplan.patient.patientGender,
      };
    }) || [];

  const config = CurrentAppConfig.RadiationDashboard.careplanDashboard;

  const defaultStatusFilterOptions = JSON.parse(JSON.stringify(config.careplanStatusFilters));
  const { hasFeature } = useContext(UserContext);
  const hasSubmitDxOnlyFeatureEnabled = hasFeature('SubmitDxOnly');

  if (hasSubmitDxOnlyFeatureEnabled) {
    defaultStatusFilterOptions.splice(1, 0, {
      name: 'DX Submitted',
      value: 'DX Submitted',
    });
  }
  const statusFilterOptions = defaultStatusFilterOptions.map((option: ConfigFilterOption): OptionType => {
    return {
      label: option.name,
      value: option.name,
    };
  });

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, DEFAULT_ROWS_PER_PAGE));
    setCurrentPage(0);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setCurrentPage(newPage);
  };

  const openCareplan = (careplanId: string) => {
    const careplan = careplans.find((careplan: Careplan) => careplan.id === careplanId);
    if (careplan) {
      history.push(redirectCareplanPath(careplan, careplan.patient.id));
    }
  };

  const toggleCareplanFilter = () => {
    setCareplanFilter(careplanFilter.includes(CareplanFilter.UNSUBMITTED) ? [] : [CareplanFilter.UNSUBMITTED]);
  };

  return (
    <Stack padding={2} width={1}>
      <Paper>
        <Stack padding={2}>
          <Typography data-testid="table-title" variant="h5" paddingBottom={2}>
            Careplan Dashboard
          </Typography>
          <Stack direction="row" alignItems="center" gap={2} height={'70px'}>
            <SearchInput
              inputValue={searchString || ''}
              id="patient-gc-search"
              placeholder="Search patient name or patient ID…"
              onSubmitSearch={(val: string) => setSearchString(val)}
            />
            <Autocomplete
              multiple
              data-testid="status-filter-autocomplete"
              options={statusFilterOptions.map((option: OptionType) => option.value)}
              value={statusFilter}
              onChange={(_, newValue) => setStatusFilter(newValue)}
              sx={{ minWidth: 200, maxWidth: 350 }}
              renderInput={(params) => {
                const { InputProps, ...other } = params;
                return (
                  <TextField
                    {...other}
                    label="Status filter"
                    variant="standard"
                    InputProps={{
                      ...InputProps,
                      startAdornment: (
                        <>
                          {statusFilter.length === 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mr: 1,
                              }}>
                              All
                            </Typography>
                          )}
                          {InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option: string, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    deleteIcon={
                      <Close
                        sx={{
                          color: 'white !important',
                          bgcolor: 'secondary.dark',
                          borderRadius: '50%',
                          fontSize: '16px',
                          padding: '2px',
                        }}
                      />
                    }
                  />
                ))
              }
              disableCloseOnSelect
              renderOption={(props, option: string, { selected }) => {
                const { key, ...optionProps } = props as { key: string };
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                      paddingBottom: '5px',
                      margin: '0px',
                    }}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                    {option}
                  </Box>
                );
              }}
            />
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Checkbox
                color="primary"
                checked={careplanFilter.includes(CareplanFilter.UNSUBMITTED)}
                onChange={toggleCareplanFilter}
              />
              <Typography sx={{ cursor: 'pointer' }} onClick={toggleCareplanFilter} variant="body1">
                Only show unsubmitted changes
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack maxHeight={'calc(100vh - 293px)'} minHeight={'calc(100vh - 293px)'} overflow="auto" padding={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={'left'}
                      width={headCell.width}
                      sx={{ backgroundColor: 'white' }}>
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonTableRows rowCount={rowsPerPage} />
                ) : careplanCount && careplans ? (
                  rows.map((row: RowProps, index: number) => (
                    <TableRow
                      data-testid={`careplan-table-row-${index}`}
                      key={row.id}
                      // sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      onClick={() => openCareplan(row.id)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}>
                      <TableCell>
                        <PatientName
                          {...row}
                          photoUrl={isDemo ? patientGenderStockImage(row.patientGender) : row.photoUrl}
                        />
                      </TableCell>
                      <TableCell>{row.department}</TableCell>
                      <TableCell>
                        <Stack direction="column" gap={0.5}>
                          <CareplanName
                            careplan={careplans.find((careplan: Careplan) => careplan.id === row.id)}
                            careplanName={row.careplanName}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Status careplanStatus={row.status} />
                      </TableCell>
                      <TableCell>
                        <Priority priority={row.priority} />
                      </TableCell>
                      <TableCell>{row.lastModified}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center">
                      <Typography padding={2} variant="body1" color="text.secondary">
                        No careplans available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
        <TablePagination
          sx={{
            '.MuiToolbar-root': {
              'align-items': 'baseline',
            },
            '.MuiTablePagination-displayedRows': {
              'margin-top': '1em',
              'margin-bottom': '1em',
            },
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIOSN}
          count={careplanCount}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
        />
      </Paper>
    </Stack>
  );
};

export default CareplanTable;
